import countries from "@/json/global-e-countries.json";
import { parseCountryCode } from "@/libs/locale/cookie";
import { EUROZONE_COUNTRY_CODES } from "@/utils/CountryUtils";

// Global-E 국가/통화 캐시 로더. docs/global-e-storefront.md §3.5
//
// 소스/갱신: `scripts/generate-global-e-countries.js` (GE `POST /Browsing/Countries`,
// merchantGUID). 수동 `npm run global-e:generate-countries` 후 커밋. 정확한 엔드포인트·필드
// 매핑은 그 스크립트 + docs §3.5 참조 (build 네트워크 의존 회피 위해 커밋 캐시로 둔다).
//
// currency = GE 의 DefaultCurrencyCode (예: AU→"AUD"). GlobalE_Data 쿠키 currencyCode 의 권위 소스.
// (기존 `CountryUtils.convertCountryCodeToCurrency` 는 비-KR/JP 를 전부 USD 로 반환 → AU 에 USD 가
//  박히는 버그가 있었음. GE 기준으로 교체.)

interface GeCountryInfo {
  /** GE DefaultCurrencyCode (ISO3, 예: "AUD"). 런타임에 쓰는 유일 필드. */
  currency: string;
  /**
   * IsOperatedByGlobalE. ⚠️ US/KR = false. 마커/지원국가 게이팅에 그대로 쓰면 US 누락 →
   * 직접 사용 금지. Fixed/Dynamic 판정과도 별개(=SupportsFixedPrices/`data-fp`). docs §3.5.
   */
  operated: boolean;
  /** SupportsFixedPrices (capability). 우리 Fixed/Dynamic 선택값과 다름 — 참고용. */
  fixedPrice: boolean;
}

const MAP = countries as Record<string, GeCountryInfo>;

// country code 정규화는 marker.ts 와 동일하게 parseCountryCode 사용(uppercase + ISO alpha-2 검증).
function lookup(countryCode?: string | null): GeCountryInfo | undefined {
  const cc = parseCountryCode(countryCode);
  return cc ? MAP[cc] : undefined;
}

/**
 * GE 기준 국가 통화(DefaultCurrencyCode). 캐시에 없으면 undefined → 호출처에서 fallback.
 * GlobalE_Data 쿠키 currencyCode 채울 때 사용.
 */
export function getGlobalECurrency(
  countryCode?: string | null,
): string | undefined {
  // 손상된 캐시(빈/비정상 통화)가 쿠키에 박히지 않도록 ISO 4217(3 대문자) 검증.
  // 통과 못 하면 undefined → 호출처에서 convertCountryCodeToCurrency fallback 으로 흐른다
  // (`??` 는 null/undefined 만 잡으므로 빈 문자열은 여기서 걸러야 안전).
  const currency = lookup(countryCode)?.currency;
  return currency && /^[A-Z]{3}$/.test(currency) ? currency : undefined;
}

/**
 * Fixed price 국가 판정 — **US / EU(유로존 21개국) / GB**. 그 외 비-KR = Dynamic.
 * **cart-info(GE 결제 `IsFixedPrice`)와 FE(가격 표시 마커 gating)가 동일 판정을 공유**해 드리프트 방지.
 *
 * Fixed = 우리가 최종가 제공(Option 1) → GE 가 변환 없이 그 숫자를 목적지 통화로 청구.
 * → **로컬 통화(EUR/GBP) 최종값이 있어야만 Fixed 가능**(Kyuri 2026-07-08 확답).
 *
 * ### 이력 — EU/GB 는 한 번 되돌렸다가 재활성(2026-08-11)
 *
 * #4975 에서 EU/GB 를 Fixed 로 넣었으나 **값이 USD base 라 GE 가 USD 숫자를 GBP/EUR 로 그대로 청구
 * → 과다청구**(GB dev 실측: 카트 £410 vs 체크아웃 £529). 당시 재무가 USD-only 라 Dynamic 으로 롤백했고,
 * 재활성 조건을 **"(a) 재무가 EUR/GBP 최종값 제공"** 으로 남겨뒀다.
 *
 * → **그 조건이 충족되어 재활성한다.** 기획 확정(2026-08-07, 안혜지): Fixed = US/EU/GB, 그 외 Dynamic.
 * mgmt `ShopProduct.euPrice`/`gbPrice` 구현 완료(admin#1430) + 실제 값 입력 완료(dev 33개 중 32개,
 * Notion 대조 일치. 나머지 1개 `tactosy-for-feet` 은 판매종료라 정상).
 *
 * ⚠️ **재발 방지**: 과다청구의 원인은 "Fixed 로 선언했는데 보낸 값이 USD" 였다. 따라서
 * `isFixedPriceCountry` 는 **`CountryUtils.convertCountryCodeToCurrency`(통화 선택) +
 * `ProductUtil.getCountryAwarePrices`(가격 선택)** 와 **항상 같은 국가 집합**을 봐야 한다.
 * 셋 다 `EUROZONE_COUNTRY_CODES` 단일 출처를 쓴다. 하나만 바꾸지 말 것.
 *
 * 상세: docs/pricing-tiers.md, memory global-e-carrier-routing-gate.
 */
export function isFixedPriceCountry(countryCode?: string | null): boolean {
  const cc = parseCountryCode(countryCode);
  if (!cc) return false;
  return cc === "US" || cc === "GB" || EUROZONE_COUNTRY_CODES.includes(cc);
}
